/** @jsxImportSource @emotion/react */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import useEvent from 'react-use-event-hook';
import { useTranslation } from 'react-i18next';
import { Button } from '@mui/material';
import { DataMap } from '@grapecity/wijmo.grid';
import CustomDialog from 'components/modals/common/CustomDialog';
import CustomGrid from 'components/grids/CustomGrid';
import { ControlBtnGroup, SubTitleGroup, SubTitleLayout } from 'components/layouts/ContentLayout';
import { IconButton } from 'components/buttons/IconSVG';
import { CrudCode } from 'models/common/Edit';
import { SetupStep } from 'models/setup/SetupStep';
import { useMessageBar } from 'hooks/useMessageBar';
import { GridStatusCellTemplate } from 'components/grids/GridStatusCellRenderer';
import { getCommonCodeNames } from 'apis/admin/CommonCode';
import { findProjectStepBySpTpCd, findSetupStep } from 'apis/setup/SetupStep';

interface Props {
  open: boolean;
  close: () => void;
  condition?: {
    elmPrjId?: string;
    spTpCd?: string;
  };
  onCallback: (result) => void;
}
const SetupStepAddRowPopup = ({ open, close, onCallback, condition, ...props }: Props) => {
  const gridRef = useRef<any>();
  const { t } = useTranslation();
  const { openMessageBar } = useMessageBar();
  const [code, setCode] = useState<any>();
  const [prjSpCds, setPrjSpCds] = useState<SetupStep[]>([]);
  const [rowData, setRowData] = useState<SetupStep[]>([]);

  useEffect(() => {
    getCommonCodes();
  }, []);

  useEffect(() => {
    handleSearch();
  }, [condition?.elmPrjId]);

  const getCommonCodes = () => {
    Promise.all([
      getCommonCodeNames('PRJ_SP_DIVS_CD'), // 셋업구분
    ]).then(([prjSpDivsCd]) => {
      setCode({
        prjSpDivsCd: prjSpDivsCd,
      });
    });
  };

  const handleSearch = () => {
    if (condition?.elmPrjId) {
      Promise.all([
        findSetupStep({ useYn: 'Y' }),
        findProjectStepBySpTpCd({
          spTpCd: condition?.spTpCd,
          elmPrjId: condition?.elmPrjId,
        }),
      ]).then(([prjSpCd, steps]) => {
        console.log('===> ', prjSpCd, steps);
        const existsSteps = (steps || []).map((o) => o.prjSpCd);
        setPrjSpCds(prjSpCd.filter((o) => !existsSteps.includes(o.prjSpCd)));
      });
    }
  };

  const handleAddRow = () => {
    const newData = {
      crudKey: CrudCode.CREATE,
      istlDivsCd: '',
      prjSpCd: '',
    };
    setRowData((prev) => [newData, ...prev]);
  };

  const handleDelRow = () => {
    const selectedRows = gridRef.current.rows.filter((r) => r.isSelected);

    if ((selectedRows || []).length < 1) {
      openMessageBar({
        type: 'error',
        content: t('com.label.삭제할 데이터를 선택해 주세요.', '삭제할 데이터를 선택해 주세요.'),
      });
      return;
    }

    const selectedIds = selectedRows
      .map((row) => {
        return parseInt(row.index!);
      })
      .reverse();

    selectedIds.forEach((item) => {
      if (rowData[item].crudKey === CrudCode.CREATE) {
        delete rowData[item];
      } else {
        rowData[item].crudKey = CrudCode.DELETE;
      }
    });

    const filteredData = rowData.filter((element) => element !== undefined);
    setRowData(filteredData);
  };

  const layoutDefinition = useMemo(() => {
    const mapPrjSpCd = new DataMap(prjSpCds || [], 'prjSpCd', 'prjSpNm');
    mapPrjSpCd.getDisplayValues = (item) => {
      // 선택한 셋업구분값에 따라 셋업단계 콤보박스 필터링
      return (prjSpCds || [])
        .filter((o) => o.istlDivsCd === item.istlDivsCd)
        .map((o) => o.prjSpNm || '');
    };
    const mapAutoCalSpCd = new DataMap(prjSpCds || [], 'prjSpCd', 'prjSpNm');
    mapAutoCalSpCd.getDisplayValues = (item) => {
      // 선택한 셋업구분값에 따라 자동계산단계 콤보박스 필터링 (+선택한 셋업단계값은 제외)
      return (prjSpCds || [])
        .filter(
          (o) => o.istlDivsCd === item.istlDivsCd && item.prjSpCd && o.prjSpCd !== item.prjSpCd
        )
        .map((o) => o.prjSpNm || '');
    };

    return [
      {
        binding: 'crudKey',
        header: String(t('com.label.상태', '상태')),
        minWidth: 43,
        width: 43,
        align: 'center',
        isReadOnly: true,
        cellTemplate: GridStatusCellTemplate,
      },
      {
        binding: 'istlDivsCd',
        header: String(t('setup.label.셋업 구분', '셋업 구분')),
        align: 'center',
        minWidth: 80,
        width: 150,
        isRequired: false,
        cssClass: 'WijmoSelect',
        dataMap: new DataMap(code?.prjSpDivsCd || [], 'cmnCd', 'cmnCdNm'),
      },
      {
        binding: 'prjSpCd',
        header: String(t('setup.label.셋업 단계 명', '셋업 단계 명')),
        align: 'left',
        minWidth: 40,
        width: '*',
        cssClass: 'WijmoSelect',
        dataMap: mapPrjSpCd,
      },
      {
        binding: 'autoCalSpCd',
        header: String(t('setup.label.자동 계산 단계', '자동 계산 단계')),
        align: 'left',
        minWidth: 40,
        width: '*',
        isRequired: false,
        cssClass: 'WijmoSelect',
        dataMap: mapAutoCalSpCd,
      },
      {
        binding: 'divsWghtScor',
        header: String(t('setup.label.자동계산\n가중치', '자동계산\n가중치')),
        align: 'center',
        minWidth: 40,
        width: 150,
        isRequired: false,
      },
      {
        binding: 'wghtScor',
        header: String(t('setup.label.단계별 가중치', '단계별 가중치')),
        align: 'center',
        minWidth: 40,
        width: 150,
        isRequired: false,
      },
    ];
  }, [code, prjSpCds]);

  const validate = () => {
    const valid = (rowData || [])
      .map((o, index) => {
        if (!o.istlDivsCd) {
          return `${index + 1} : ${t(
            'setup.label.셋업 구분을 선택해 주세요.',
            '셋업 구분을 선택해 주세요.'
          )}`;
        }
        if (!o.prjSpCd) {
          return `${index + 1} : ${t(
            'setup.label.셋업 단계을 선택해 주세요.',
            '셋업 단계을 선택해 주세요.'
          )}`;
        }
      })
      .filter((element) => element !== undefined);

    if (valid.length) {
      openMessageBar({ type: 'error', content: valid.join('<br/>') });
      return false;
    }
    return true;
  };

  const handleConfirm = () => {
    if (!validate()) return;
    onCallback && onCallback(rowData);
    close();
  };

  const onBeginningEdit = useEvent((grid, e) => {
    const col = grid.columns[e.col];
    const binding = col.binding;
    const item = grid.rows[e.row].dataItem;
  });

  const onCellEditEnding = useEvent((grid, e) => {
    const binding = grid.columns[e.col].binding;
    const item = grid.rows[e.row].dataItem;
    const oldVal = grid.getCellData(e.row, e.col);
    const newVal = grid.activeEditor?.value;
    // 셋업 구분
    if ('istlDivsCd' === binding) {
      const newIstlDivsCd =
        (code?.prjSpDivsCd || []).find((o) => o.cmnCdNm === newVal)?.cmnCd || '';
      // 값이 변한 경우 초기화
      if (oldVal !== newIstlDivsCd) {
        item.prjSpCd = '';
        item.autoCalSpCd = '';
        item.divsWghtScor = '';
      }
    }
    // 셋업 단계
    else if ('prjSpCd' === binding) {
      const newPrjSpCd = (prjSpCds || []).find((o) => o.prjSpNm === newVal)?.prjSpCd || '';
      // 변경한 셋업 단계가 자동계산단계와 일치하는 경우 초기화
      if (item.autoCalSpCd === newPrjSpCd) {
        item.autoCalSpCd = '';
        item.divsWghtScor = '';
      }
    }
    // 자동계산단계
    else if ('autoCalSpCd' === binding) {
      const newAutoCalSpCd = (prjSpCds || []).find((o) => o.prjSpNm === newVal)?.prjSpCd || '';
      // 자동계산단계가 없는 경우 자동계산 가중치 초기화
      if (!newAutoCalSpCd) {
        item.divsWghtScor = '';
      }
    }
    // 자동계산 가중치, 단계별 가중치, 환산 가중치
    else if (['divsWghtScor', 'wghtScor'].includes(binding)) {
      // 숫자만 입력 가능
      if (newVal) {
        if (!/^[0-9]+$/.test(newVal)) {
          e.cancel = true;
          return;
        }
      }
    }
  });

  const onCellEditEnded = useEvent((grid, e) => {
    const col = grid.columns[e.col];
    const binding = col.binding;
    const item = grid.rows[e.row].dataItem;
  });

  const onInitialized = (grid) => {
    grid.autoRowHeights = true;
    gridRef.current = grid;
    grid.formatItem.addHandler((s, e) => {
      const item = s.rows[e.row]?.dataItem;
      if (e.panel === s.columnHeaders) {
        e.cell.style.display = 'flex';
        e.cell.style.alignItems = 'center';
        e.cell.style.justifyContent = 'center';
        const text = e.cell.textContent ?? '';
        if (text.includes('\n')) {
          e.cell.innerHTML = text.replace(/\n/g, '<br/>');
        }
      }
    });
  };

  const dialogButtons = [
    <Button
      key={'confirm'}
      css={IconButton.button}
      className="confirm"
      onClick={handleConfirm}
      disableRipple
    >
      {t('com.button.선택', '선택')}
    </Button>,
  ];

  return (
    <CustomDialog
      title={String(t('setup.label.셋업 단계 행추가', '셋업 단계 행추가'))}
      open={open}
      onClose={close}
      onCancel={close}
      buttons={dialogButtons}
    >
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('setup.label.셋업 단계', '셋업 단계')}</h3>
          <span className="total">
            {t('com.label.총', '총')} <span>{(rowData || []).length.toLocaleString()}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
        <ControlBtnGroup>
          <Button css={IconButton.button} className="addRow" onClick={handleAddRow} disableRipple>
            {t('com.button.행추가', '행추가')}
          </Button>
          <Button css={IconButton.button} className="delRow" onClick={handleDelRow} disableRipple>
            {t('com.button.행삭제', '행삭제')}
          </Button>
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        height={500}
        isFilter={false}
        allowPinning={false}
        beginningEdit={onBeginningEdit}
        cellEditEnding={onCellEditEnding}
        cellEditEnded={onCellEditEnded}
        initialized={onInitialized}
      />
    </CustomDialog>
  );
};

export default SetupStepAddRowPopup;
